import { Injectable } from '@angular/core';

import { catchError, first, map, Observable, of } from 'rxjs';

import { Api } from '../services/api';
import { LoadingService } from '../loading/loading.service';

import { ActorStore } from './actor.store';
import { Actor, ActorTypes } from './actor.model';
import { AuthService, User } from '@auth0/auth0-angular';
import { CompanyOrderStatus } from '../models/company';

export const ORDERS_MODULE_KEY = 'orders';
export const INVENTORY_MODULE_KEY = 'inventory';

export interface ModuleSettings {
  [k: string]: boolean;
}

export interface CanOptions {
  actions: string[];
  resource: string;
  userId?: string | null;
  allowActorType?: ActorTypes | ActorTypes[];
}

@Injectable({ providedIn: 'root' })
export class ActorService {
  constructor(
    private store: ActorStore,
    private api: Api,
    private loader: LoadingService,
    private authService: AuthService
  ) {}

  getUserInfo(): Observable<Actor|null> {
    return this.api.get('auth/userinfo').pipe(map((actor: Actor) => {
      this.store.update(actor);
      return actor;
    }), catchError(error => {
      const message = typeof error === 'string' ? error : error?.message ?? '';
      if (message.includes('Missing Refresh Token')) {
        this.authService.logout();
      }
      return of(null);
    }));
  }

  isAuthenticated(): Observable<boolean> {
    return this.authService.isAuthenticated$;
  }

  getAuth0User(): Observable<User|null|undefined> {
    return this.authService.user$;
  }

  getToken(): Observable<string> {
    return this.authService.getAccessTokenSilently();
  }

  logout(returnTo: string|null = null): void {
    if (returnTo) this.authService.logout({ logoutParams: { returnTo } });
    else this.authService.logout();
    this.store.reset();
  }

  checkInvite(token: string, cid: string): Observable<{ status: boolean, email: string}> {
    return this.api.get(`auth/checkinvite/${token}?cid=${cid}`).pipe(first(), this.loader.set('check-invite'));
  }

  register(token: string, cid: string, password: string, conditionsRead: boolean) {
    return this.api.post('auth/password/set', { token, cid, password, conditionsRead });
  }

  acceptInvite(token: string, cid: string) {
    return this.api.get(`auth/accept/${token}?cid=${cid}`);
  }

  select(): Observable<Actor> {
    return this.store.select();
  }

  get(): Actor {
    return this.store.get();
  }

  hasCompany(): Observable<boolean> {
    return this.select().pipe(map(actor => !!(actor.company)));
  }

  hasOrdersEnabled(): boolean {
    const modules: ModuleSettings = this.get().modules;
    return ORDERS_MODULE_KEY in modules && modules[ORDERS_MODULE_KEY] === true;
  }

  /**
   * Mirrors OrdersGuard access: the Orders module must be enabled and, for guests,
   * their company must have an active order status.
   */
  canAccessOrders(): boolean {
    const actor: Actor = this.get();
    if (!actor?.modules || !this.hasOrdersEnabled()) {
      return false;
    }

    if (actor.type === ActorTypes.GUEST) {
      return actor.company?.orderStatus === CompanyOrderStatus.ACTIVE;
    }

    return true;
  }

  hasInventoryEnabled(): boolean {
    const modules: ModuleSettings = this.get().modules;
    return INVENTORY_MODULE_KEY in modules && modules[INVENTORY_MODULE_KEY] === true;
  }

  can({ actions, resource, userId = null, allowActorType = [] }: CanOptions): boolean {
    const actor = this.get();
    if (!actor.id) {
      return false;
    }
    const { role, type } = actor;
    const allowedTypes = Array.isArray(allowActorType) ? allowActorType : [allowActorType];
    if (type === ActorTypes.SUPER || allowedTypes.includes(type as ActorTypes)) {
      return true;
    }
    if (type === ActorTypes.GUEST || !role?.permissions[resource]) {
      return false;
    }

    return role?.permissions[resource].some((permission: string) => {
      const [action, scope] = permission.split(':');
      return (
        scope === 'owner' && userId && actor.id !== userId
          ? false
          : actions.includes(action)
      );
    });
  }

  get hasProfileViewAllInFileVault() {
    const actor = this.get();

    if (actor.type === ActorTypes.SUPER) {
      return true;
    }

    const permissions = actor.role ? actor.role.permissions : null;
    if (!permissions) {
      return false;
    }

    return 'file-vault' in permissions
      && permissions['file-vault'].includes('view:all');
  }
}
